/**
 * Security Folder Expansion - Handles loading security objects when folders are expanded
 */

import type { DatabaseExplorerNode, FolderTreeNode } from '../../explorer/models/DatabaseTreeModels';
import {
  createSecurityFolderErrorNode,
  fetchSecurityFolderChildren,
  isSecurityLeafFolderNodeType,
  needsDatabaseForSecurityFolder,
} from './securityFolderExpansionUtils';

export type { SecurityLeafFolderNodeType } from './securityFolderExpansionUtils';
export { isSecurityLeafFolderNodeType } from './securityFolderExpansionUtils';

export interface LoadSecurityFolderOptions {
  node: FolderTreeNode;
  updateNode: (nodeId: string, updates: Partial<DatabaseExplorerNode>) => void;
  onFetchError?: (connectionId: string, errorMessage: string) => void;
  forceReload?: boolean;
}

export async function loadSecurityFolder(options: LoadSecurityFolderOptions): Promise<void> {
  const { node, updateNode, onFetchError, forceReload = false } = options;

  if (!isSecurityLeafFolderNodeType(node.type)) return;

  const { connectionId, database } = node.metadata ?? {};
  if (!connectionId) return;

  if (!forceReload && node.children?.length) {
    const hasError = node.children.some((child) => child.type === 'error');
    if (!hasError) return;
  }

  const needsDatabase = needsDatabaseForSecurityFolder(node.type);
  if (needsDatabase && !database) {
    updateNode(node.id, {
      children: [
        createSecurityFolderErrorNode({
          connectionId,
          database,
          folderType: node.type,
          parentId: node.id,
          errorMessage: 'Database is required for this folder',
        }),
      ],
      isLoading: false,
    });
    return;
  }

  const api = window.electronAPI?.securityManagement;
  if (!api) {
    updateNode(node.id, {
      children: [
        createSecurityFolderErrorNode({
          connectionId,
          database,
          folderType: node.type,
          parentId: node.id,
          errorMessage: 'Security management API not available',
        }),
      ],
      isLoading: false,
    });
    return;
  }

  updateNode(node.id, { isLoading: true, children: [] });

  try {
    const children = await fetchSecurityFolderChildren({
      api,
      folderType: node.type,
      connectionId,
      database,
      parentId: node.id,
    });
    updateNode(node.id, {
      children,
      isLoading: false,
    });
  } catch (error) {
    const message = error instanceof Error ? error.message : undefined;
    if (message) onFetchError?.(connectionId, message);

    updateNode(node.id, {
      children: [createSecurityFolderErrorNode({ connectionId, database, folderType: node.type, parentId: node.id, errorMessage: message })],
      isLoading: false,
    });
  }
}
